import * as React from 'react';
import { Languages } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export const META_GUIDE_DEMO_OPTIONS = [
  {
    id: 'english-hindi',
    label: 'English / Hindi',
    url: 'https://app.wenext.ai/book/english-hindi-demos',
  },
  {
    id: 'telugu',
    label: 'Telugu',
    url: 'https://app.wenext.ai/book/wenext-platform-walkthrough',
  },
] as const;

export type MetaGuideDemoUrl = (typeof META_GUIDE_DEMO_OPTIONS)[number]['url'];

interface DemoLanguageMenuProps {
  children: React.ReactNode;
  onSelect: (url: MetaGuideDemoUrl) => void;
  testIdPrefix: string;
  modal?: boolean;
  /**
   * Extra classes for the portalled menu. Needed when the trigger sits inside a
   * stacking context above the default z-80 — the plan-expiry gate is z-100, so
   * without this the menu opens BEHIND the modal.
   */
  contentClassName?: string;
}

export function DemoLanguageMenu({
  children,
  onSelect,
  testIdPrefix,
  modal = true,
  contentClassName,
}: DemoLanguageMenuProps) {
  return (
    <DropdownMenu modal={modal}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      {/* Styled to match the sidebar workspace switcher panel. */}
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        collisionPadding={8}
        className={cn(
          'z-[80] w-[240px] max-w-[calc(100vw-16px)] rounded-2xl border border-[#EAECF0] bg-white p-2 text-[14px] shadow-[0_12px_32px_rgba(16,24,40,0.12)]',
          contentClassName,
        )}
      >
        <DropdownMenuLabel className="px-1.5 pb-1.5 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
          Choose a language
        </DropdownMenuLabel>
        {META_GUIDE_DEMO_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.id}
            data-testid={`${testIdPrefix}-${option.id}`}
            className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-0 text-[14px] font-medium text-[#475467] hover:bg-[#F9FAFB] focus:bg-[#F9FAFB] focus:text-[#101828] data-[highlighted]:bg-[#F9FAFB] data-[highlighted]:text-[#101828]"
            onSelect={() => onSelect(option.url)}
          >
            <Languages className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">{option.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
